import React, { useMemo, useState, useEffect } from 'react';
import clsx from 'clsx';
import { Checkbox } from './index';
import { AiOutlineDown, AiOutlineUp } from 'react-icons/ai';
import styles from './Filter.module.scss';
import { translations } from '@utils/translations';

/**
 * A collection of arrow components.
 * This object helps out with selecting the proper component
 * based on the direction of the arrow.
 */
const arrowComponents = {
    up: () => <AiOutlineUp />,
    down: () => <AiOutlineDown />
};

/**
 * Just like the arrows, we determine the direction of the
 * arrows based on if the filters should be shown or not.
 * Since objects don't support booleans as keys, we use a Map.
 */
const directionMap = new Map();
directionMap.set(true, 'up');
directionMap.set(false, 'down');

const FilterModel = ({
    title,
    options = [],
    active = false,
    hasSelectedBrand = false
}) => {
    const [shouldFiltersShow, setShouldFiltersShow] = useState(
        active || hasSelectedBrand
    );
    const [arrowDirection, setArrowDirection] = useState(
        directionMap.get(active || hasSelectedBrand)
    );
    const [numberOfitemsShown, setNumberOfItemsToShown] = useState(8);
    const [isMore, setIsMore] = useState(false);

    const translate = s => translations['nl'][s] || s;

    /**
     * Open and close the filters when clicking the header.
     */
    const toggleFilters = () => {
        if (hasSelectedBrand) {
            const direction = directionMap.get(!shouldFiltersShow);
            setArrowDirection(direction);
            setShouldFiltersShow(!shouldFiltersShow);
        }
    };

    /**
     * Toggle show more options.
     */
    const toggleShowMoreOrLess = () => {
        if (isMore === false) {
            setNumberOfItemsToShown(options.length);
            setIsMore(true);
        } else {
            setNumberOfItemsToShown(8);
            setIsMore(false);
        }
    };

    const itemsToShow = useMemo(
        () =>
            options
                .slice(0, numberOfitemsShown)
                .map((option, index) => (
                    <Checkbox
                        key={title + index}
                        title={title}
                        isSelected={option.selected}
                        name={option.name}
                        count={option.count}
                        value={option.value}
                    />
                )),
        [options, numberOfitemsShown, title]
    );

    const selectedOptions = useMemo(
        () => options.filter(({ selected }) => selected === true),
        [options]
    );

    const activeItems = useMemo(
        () => selectedOptions.map(({ name }) => name).join(', '),
        [selectedOptions]
    );

    useEffect(() => {
        const direction = directionMap.get(hasSelectedBrand);
        setShouldFiltersShow(hasSelectedBrand);
        setArrowDirection(direction);
    }, [hasSelectedBrand, setArrowDirection]);

    /**
     * Select the correct arrow component based on the arrowDirection.
     *
     * @var {JSX.Element}
     */
    const ArrowComponent = arrowComponents[arrowDirection];

    if (!options.length) {
        return null;
    }

    return (
        <div
            className={clsx(
                `op-filter-item op-filter`,
                !hasSelectedBrand && 'op-filter-item--disabled',
                styles.item
            )}
        >
            <div
                className={`op-filter__header ${styles.header}`}
                onClick={toggleFilters}
            >
                <span className={`op-filter__title`}>{translate(title)}</span>

                <ArrowComponent />
            </div>

            <div className={`op-filter__options`}>
                {shouldFiltersShow && hasSelectedBrand && (
                    <>
                        <ul className={`op-filter__items`}>
                            {itemsToShow.length && itemsToShow}
                        </ul>

                        {options.length > 8 && (
                            <button
                                type="button"
                                className={`op-filter__more`}
                                onClick={toggleShowMoreOrLess}
                            >
                                {isMore ? 'Toon minder -' : 'Toon meer +'}
                            </button>
                        )}
                    </>
                )}

                {!hasSelectedBrand && (
                    <span className={`op-filter__more-text`}>
                        Kies eerst een merk
                    </span>
                )}

                {hasSelectedBrand && !shouldFiltersShow && (
                    <div className={`op-filter__closed`}>
                        {selectedOptions.length === 0 && (
                            <span
                                className={`op-filter__closed-text`}
                                onClick={toggleFilters}
                            >
                                {translate(`Select ${title}`)}
                            </span>
                        )}

                        {selectedOptions.length !== 0 && (
                            <span className={`op-filter__closed-list`}>
                                {activeItems}
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FilterModel;
